// src/pages/SettingsPage.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Sidebar from '../components/SideBar';
import AbsoluteNavigation from '../components/AbsoluteNavigation';
import { auth, db } from '../firebase';
import {
  doc,
  updateDoc,
  deleteField,
  serverTimestamp
} from 'firebase/firestore';
import '../styles/SettingsPage.css';
import { useSessionStorage } from '../contexts/SessionStorageContext';
import { useTheme } from '../contexts/ThemeContext';
import { MdDelete, MdLightMode, MdDarkMode, MdWaves, MdEmail, MdAutoAwesome, MdSave, MdCheck, MdBusiness, MdBlock } from 'react-icons/md';
import ProfilePictureCropper from './ProfilePictureCropper';
import BlockedContactsModal from './BlockedContactsModal';
import {
  buildProfessionalSignature,
  getSignatureOrganizationDefaults,
  resizeSignatureLogoFile
} from '../utils/signatureBuilder';
import SignaturePreview from './SignaturePreview';
import SheetAccessAccounts from './SheetAccessAccounts';

function buildProfileSignature({ displayName, organizationName, email }) {
  const lines = [
    displayName,
    organizationName,
    email
  ]
    .map(value => (value || '').trim())
    .filter(Boolean);

  return Array.from(new Set(lines)).join('\n');
}

const emptySignatureBuilderFields = {
  name: '',
  title: '',
  team: '',
  licenseLine: '',
  phone: '',
  email: '',
  company: '',
  website: '',
  location: '',
  linkedinUrl: '',
  logoDataUrl: ''
};

function mergeProfessionalSignatureDefaults(savedFields = {}, defaultFields = {}, fallbackEmail = '') {
  return Object.keys(emptySignatureBuilderFields).reduce((acc, key) => {
    acc[key] = savedFields[key] || defaultFields[key] || (key === 'email' ? fallbackEmail : '');
    return acc;
  }, {});
}

export default function SettingsPage() {
  const { sessionData, updateSessionData } = useSessionStorage();
  const { theme, setThemeMode } = useTheme();
  const user = auth.currentUser;

  const [preferredDisplayName, setPreferredDisplayName] = useState('');
  const [organizationName, setOrganizationName]       = useState('');
  const [profilePic, setProfilePic]                   = useState('');
  const [profilePicShape, setProfilePicShape]         = useState('circle');
  const [showCropper, setShowCropper]                 = useState(false);
  const [tempImageSrc, setTempImageSrc]               = useState('');
  const [emailSignature, setEmailSignature] = useState('');
  const [signatureMode, setSignatureMode] = useState('none'); // 'none', 'custom', 'professional'
  const [signatureBuilderFields, setSignatureBuilderFields] = useState(emptySignatureBuilderFields);

  // Use clients from session storage to avoid flash of pulse animation
  const clients = sessionData.clients || [];
  const isLoadingClients = sessionData.clients === null;
  const [isLoadingSignature, setIsLoadingSignature] = useState(true);
  const [isSavingSignature, setIsSavingSignature] = useState(false);
  const [signatureSaved, setSignatureSaved] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState(''); // 'saving', 'saved', ''
  const [showBlockedContacts, setShowBlockedContacts] = useState(false);

  const profileEmail = user?.email || sessionData.userProfile?.email || '';

  const buildSignatureFromCurrentProfile = useCallback(() => buildProfileSignature({
    displayName: preferredDisplayName || user?.displayName || sessionData.userProfile?.displayName || '',
    organizationName,
    email: profileEmail
  }), [preferredDisplayName, user?.displayName, sessionData.userProfile?.displayName, organizationName, profileEmail]);

  const getProfessionalSignatureFields = useCallback((fields = signatureBuilderFields) => ({
    ...emptySignatureBuilderFields,
    ...fields
  }), [signatureBuilderFields]);

  // Refs for debouncing auto-save
  const displayNameTimeoutRef = useRef(null);
  const orgNameTimeoutRef = useRef(null);
  const initialLoadRef = useRef(true);
  const signatureTextareaRef = useRef(null);

  // Auto-resize signature textarea
  const resizeSignatureTextarea = useCallback(() => {
    const textarea = signatureTextareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(textarea.scrollHeight, 120)}px`;
    }
  }, []);

  // Auto-save function for display name and org name
  const autoSaveField = useCallback(async (field, value) => {
    if (!user) return;
    setAutoSaveStatus('saving');
    try {
      const ref = doc(db, 'users', user.uid);
      await updateDoc(ref, {
        [field]: value,
        updatedAt: serverTimestamp(),
      });

      // Update session storage
      if (field === 'preferredDisplayName') {
        updateSessionData('userProfile', {
          displayName: value,
          profilePic,
          profilePicShape
        });
      } else if (field === 'organizationName') {
        updateSessionData('organizationName', value);
      }

      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(''), 2000);
    } catch (err) {
      console.error('Auto-save failed:', err);
      setAutoSaveStatus('');
    }
  }, [user, profilePic, profilePicShape, updateSessionData]);

  // Load settings from session storage (context handles fetching)
  useEffect(() => {
    if (!user) {
      setIsLoadingSignature(false);
      return;
    }

    // Initialize from session storage
    if (sessionData.userProfile) {
      const organizationDefaults = getSignatureOrganizationDefaults(profileEmail);
      const professionalDefaults = organizationDefaults.professionalSignature || {};
      setPreferredDisplayName(sessionData.userProfile.displayName || '');
      setProfilePic(sessionData.userProfile.profilePic || sessionData.userProfile.profilePicture || '');
      setProfilePicShape(sessionData.userProfile.profilePicShape || 'circle');
      // Signature is now included in userProfile from context
      setEmailSignature(sessionData.userProfile.emailSignature || '');
      setSignatureMode(sessionData.userProfile.signatureMode || 'none');
      setSignatureBuilderFields(mergeProfessionalSignatureDefaults(
        sessionData.userProfile.professionalSignature || {},
        professionalDefaults,
        profileEmail
      ));
      setIsLoadingSignature(false);
    } else {
      const organizationDefaults = getSignatureOrganizationDefaults(profileEmail);
      setSignatureBuilderFields(mergeProfessionalSignatureDefaults(
        {},
        organizationDefaults.professionalSignature || {},
        profileEmail
      ));
      setIsLoadingSignature(false);
    }
    if (sessionData.organizationName !== null) {
      const organizationDefaults = getSignatureOrganizationDefaults(profileEmail);
      setOrganizationName(sessionData.organizationName || organizationDefaults.organizationName || '');
    }
  }, [user, sessionData.userProfile, sessionData.organizationName, profileEmail]);

  // Mark initial load complete after first render
  useEffect(() => {
    const timer = setTimeout(() => {
      initialLoadRef.current = false;
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Resize signature textarea when content changes
  useEffect(() => {
    resizeSignatureTextarea();
  }, [emailSignature, isLoadingSignature, resizeSignatureTextarea]);

  // Handle display name change with debounced auto-save
  function handleDisplayNameChange(e) {
    const value = e.target.value;
    setPreferredDisplayName(value);

    // Don't auto-save during initial load
    if (initialLoadRef.current) return;

    // Clear existing timeout
    if (displayNameTimeoutRef.current) {
      clearTimeout(displayNameTimeoutRef.current);
    }

    // Debounce: save after 800ms of no typing
    displayNameTimeoutRef.current = setTimeout(() => {
      autoSaveField('preferredDisplayName', value);
    }, 800);
  }

  // Handle org name change with debounced auto-save
  function handleOrgNameChange(e) {
    const value = e.target.value;
    setOrganizationName(value);

    // Don't auto-save during initial load
    if (initialLoadRef.current) return;

    // Clear existing timeout
    if (orgNameTimeoutRef.current) {
      clearTimeout(orgNameTimeoutRef.current);
    }

    // Debounce: save after 800ms of no typing
    orgNameTimeoutRef.current = setTimeout(() => {
      autoSaveField('organizationName', value);
    }, 800);
  }

  // handle file → open cropper
  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setTempImageSrc(reader.result);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  }

  // handle cropper completion
  function handleCropComplete(croppedImage, shape) {
    setProfilePic(croppedImage);
    setProfilePicShape(shape);
    setShowCropper(false);
    setTempImageSrc('');

    // Auto-save profile pic immediately
    if (user) {
      const ref = doc(db, 'users', user.uid);
      updateDoc(ref, {
        profilePic: croppedImage,
        profilePicShape: shape,
        updatedAt: serverTimestamp(),
      });
      updateSessionData('userProfile', {
        displayName: preferredDisplayName,
        profilePic: croppedImage,
        profilePicShape: shape
      });
    }
  }

  // Save signature and mode
  async function handleSaveSignature() {
    if (!user) return;
    setIsSavingSignature(true);
    setSignatureSaved(false);

    try {
      const professionalSignature = getProfessionalSignatureFields();
      const signatureToSave = signatureMode === 'professional'
        ? buildProfessionalSignature(professionalSignature)
        : emailSignature;

      if (signatureToSave !== emailSignature) {
        setEmailSignature(signatureToSave);
      }

      const ref = doc(db, 'users', user.uid);
      const updatePayload = {
        emailSignature: signatureToSave,
        signatureMode,
        updatedAt: serverTimestamp(),
      };

      if (signatureMode === 'professional') {
        updatePayload.professionalSignature = professionalSignature;
      }

      await updateDoc(ref, updatePayload);

      // Update session storage
      updateSessionData('emailSignature', signatureToSave);
      updateSessionData('signatureMode', signatureMode);
      if (signatureMode === 'professional') {
        updateSessionData('userProfile', {
          emailSignature: signatureToSave,
          signatureMode,
          professionalSignature
        });
      }

      setSignatureSaved(true);
      setTimeout(() => setSignatureSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save signature:', err);
      alert('Failed to save signature. Please try again.');
    } finally {
      setIsSavingSignature(false);
    }
  }

  // Build a starter signature from the signed-in user's own profile.
  function handleUseProfileSignature() {
    const profileFields = {
      ...signatureBuilderFields,
      name: preferredDisplayName || user?.displayName || sessionData.userProfile?.displayName || '',
      email: profileEmail,
      company: organizationName
    };
    setSignatureBuilderFields(profileFields);
    const builtSignature = signatureMode === 'professional'
      ? buildProfessionalSignature(profileFields)
      : buildSignatureFromCurrentProfile();
    setEmailSignature(builtSignature);
  }

  function handleSignatureBuilderFieldChange(field, value) {
    setSignatureBuilderFields(prev => ({
      ...prev,
      [field]: value
    }));
  }

  function handleBuildProfessionalSignature() {
    setEmailSignature(buildProfessionalSignature(getProfessionalSignatureFields()));
  }

  async function handleSignatureLogoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file for the signature logo.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert('Please upload a logo smaller than 2 MB.');
      return;
    }
    try {
      const resizedLogo = await resizeSignatureLogoFile(file);
      handleSignatureBuilderFieldChange('logoDataUrl', resizedLogo);
    } catch (err) {
      alert(err.message || 'Could not prepare the signature logo.');
    } finally {
      e.target.value = '';
    }
  }

  function handleRemoveSignatureLogo() {
    handleSignatureBuilderFieldChange('logoDataUrl', '');
  }

  // Clear signature
  function handleClearSignature() {
    setEmailSignature('');
    if (signatureMode === 'professional') {
      setSignatureBuilderFields(emptySignatureBuilderFields);
    }
  }

  // remove profilePic field from Firestore and state
  async function handleRemovePic() {
    if (!user) return;
    const ref = doc(db, 'users', user.uid);
    await updateDoc(ref, {
      profilePic: deleteField(),
      updatedAt:  serverTimestamp(),
    });
    setProfilePic('');
    
    // Update session storage
    updateSessionData('userProfile', { 
      displayName: preferredDisplayName, 
      profilePic: '' 
    });
  }

  return (
    <div className="app-layout">
      <Sidebar clientCount={clients.length} recentlyCreatedClient={false} isLoadingClients={isLoadingClients} />

      <main 
        className="main-content settings-page"
      >
        <AbsoluteNavigation />

        <h2>Settings</h2>

        {/* Theme Selection Section */}
        <div className="theme-section">
          <h3>Appearance</h3>
          <div className="theme-selector-container">
            <span className="theme-label">Theme</span>
            <div className="theme-selector">
              <button
                type="button"
                className={`theme-btn ${theme === 'light' ? 'theme-btn--active' : ''}`}
                onClick={() => setThemeMode('light')}
                aria-label="Light theme"
              >
                <MdLightMode size={22} />
                <span>Light</span>
              </button>
              <button
                type="button"
                className={`theme-btn ${theme === 'dark' ? 'theme-btn--active' : ''}`}
                onClick={() => setThemeMode('dark')}
                aria-label="Dark theme"
              >
                <MdDarkMode size={22} />
                <span>Dark</span>
              </button>
              <button
                type="button"
                className={`theme-btn ${theme === 'ocean' ? 'theme-btn--active' : ''}`}
                onClick={() => setThemeMode('ocean')}
                aria-label="Ocean theme"
              >
                <MdWaves size={22} />
                <span>Ocean</span>
              </button>
            </div>
          </div>
        </div>

        {/* Email Signature Section */}
        <div className="signature-section">
          <h3>
            <MdEmail size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
            Email Signature
          </h3>
          <p className="signature-description">
            Choose how your emails are signed. <strong>Remember to save your changes.</strong>
          </p>

          {/* Signature Mode Selector */}
          <div className="signature-mode-selector">
            <label
              className={`signature-mode-option ${signatureMode === 'professional' ? 'signature-mode-option--active' : ''}`}
              onClick={() => setSignatureMode('professional')}
            >
              <input
                type="radio"
                name="signatureMode"
                value="professional"
                checked={signatureMode === 'professional'}
                onChange={(e) => setSignatureMode(e.target.value)}
              />
              <div className="signature-mode-content">
                <MdBusiness size={20} />
                <div className="signature-mode-text">
                  <strong>Professional</strong>
                  <span>Clean business signature from your saved details</span>
                </div>
              </div>
            </label>

            <label
              className={`signature-mode-option ${signatureMode === 'custom' ? 'signature-mode-option--active' : ''}`}
              onClick={() => setSignatureMode('custom')}
            >
              <input
                type="radio"
                name="signatureMode"
                value="custom"
                checked={signatureMode === 'custom'}
                onChange={(e) => setSignatureMode(e.target.value)}
              />
              <div className="signature-mode-content">
                <MdAutoAwesome size={20} />
                <div className="signature-mode-text">
                  <strong>Custom</strong>
                  <span>Write a fully custom text signature below</span>
                </div>
              </div>
            </label>

            <label
              className={`signature-mode-option ${signatureMode === 'none' ? 'signature-mode-option--active' : ''}`}
              onClick={() => setSignatureMode('none')}
            >
              <input
                type="radio"
                name="signatureMode"
                value="none"
                checked={signatureMode === 'none'}
                onChange={(e) => setSignatureMode(e.target.value)}
              />
              <div className="signature-mode-content">
                <MdDelete size={20} />
                <div className="signature-mode-text">
                  <strong>None</strong>
                  <span>No signature attached to emails</span>
                </div>
              </div>
            </label>
          </div>

          {/* Signature Editor */}
          {signatureMode !== 'none' && (
            isLoadingSignature ? (
              <div className="signature-editor">
                <div className="signature-skeleton">
                  <div className="signature-skeleton__line signature-skeleton__line--long"></div>
                  <div className="signature-skeleton__line signature-skeleton__line--medium"></div>
                  <div className="signature-skeleton__line signature-skeleton__line--short"></div>
                  <div className="signature-skeleton__line signature-skeleton__line--medium"></div>
                </div>
              </div>
            ) : (
              <div className="signature-editor">
                {signatureMode === 'professional' && (
                  <div className="signature-builder" aria-label="Professional signature builder">
                    <div className="signature-builder__header">
                      <div>
                        <h4>Professional Signature Builder</h4>
                        <p>Fill the fields you want, upload an optional logo, review the preview, then save.</p>
                        <p className="signature-builder__assurance">
                          Professional signatures are created from your fields below. SiteSift will not reuse another user's saved signature.
                        </p>
                      </div>
                    </div>
                    <div className="signature-builder__grid">
                      <label>
                        Signature name
                        <input
                          type="text"
                          value={signatureBuilderFields.name}
                          onChange={(e) => handleSignatureBuilderFieldChange('name', e.target.value)}
                          placeholder="Your full name"
                        />
                      </label>
                      <label>
                        Signature title
                        <input
                          type="text"
                          value={signatureBuilderFields.title}
                          onChange={(e) => handleSignatureBuilderFieldChange('title', e.target.value)}
                          placeholder="Your title"
                        />
                      </label>
                      <label>
                        Team or department
                        <input
                          type="text"
                          value={signatureBuilderFields.team}
                          onChange={(e) => handleSignatureBuilderFieldChange('team', e.target.value)}
                          placeholder="Team or department"
                        />
                      </label>
                      <label>
                        License line
                        <input
                          type="text"
                          value={signatureBuilderFields.licenseLine}
                          onChange={(e) => handleSignatureBuilderFieldChange('licenseLine', e.target.value)}
                          placeholder="License # (optional)"
                        />
                      </label>
                      <label>
                        Phone
                        <input
                          type="text"
                          value={signatureBuilderFields.phone}
                          onChange={(e) => handleSignatureBuilderFieldChange('phone', e.target.value)}
                          placeholder="+1 555 0100"
                        />
                      </label>
                      <label>
                        Signature email
                        <input
                          type="email"
                          value={signatureBuilderFields.email}
                          onChange={(e) => handleSignatureBuilderFieldChange('email', e.target.value)}
                          placeholder="name@company.com"
                        />
                      </label>
                      <label>
                        Signature company
                        <input
                          type="text"
                          value={signatureBuilderFields.company}
                          onChange={(e) => handleSignatureBuilderFieldChange('company', e.target.value)}
                          placeholder="Company name"
                        />
                      </label>
                      <label>
                        Website
                        <input
                          type="text"
                          value={signatureBuilderFields.website}
                          onChange={(e) => handleSignatureBuilderFieldChange('website', e.target.value)}
                          placeholder="company.com"
                        />
                      </label>
                      <label>
                        Location
                        <input
                          type="text"
                          value={signatureBuilderFields.location}
                          onChange={(e) => handleSignatureBuilderFieldChange('location', e.target.value)}
                          placeholder="City, ST"
                        />
                      </label>
                      <label>
                        LinkedIn URL
                        <input
                          type="url"
                          value={signatureBuilderFields.linkedinUrl}
                          onChange={(e) => handleSignatureBuilderFieldChange('linkedinUrl', e.target.value)}
                          placeholder="https://www.linkedin.com/in/your-profile"
                        />
                      </label>
                    </div>
                    <div className="signature-builder__logo-row">
                      <label className="signature-builder__logo-label">
                        Signature logo
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/gif,image/webp"
                          onChange={handleSignatureLogoChange}
                        />
                      </label>
                      {signatureBuilderFields.logoDataUrl ? (
                        <div className="signature-builder__logo-preview">
                          <img src={signatureBuilderFields.logoDataUrl} alt="Signature logo preview" />
                          <button
                            type="button"
                            className="btn btn--clear-signature"
                            onClick={handleRemoveSignatureLogo}
                          >
                            <MdDelete size={16} />
                            Remove logo
                          </button>
                        </div>
                      ) : (
                        <div className="signature-builder__logo-empty">No logo uploaded</div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn btn--build-signature"
                      onClick={handleBuildProfessionalSignature}
                    >
                      <MdAutoAwesome size={16} />
                      Build Professional Signature
                    </button>
                    <div className="signature-preview-panel" aria-label="Generated professional signature preview">
                      <span className="signature-preview-label">Preview</span>
                      <SignaturePreview
                        signatureMode="professional"
                        emailSignature={buildProfessionalSignature(getProfessionalSignatureFields()) || emailSignature}
                      />
                    </div>
                  </div>
                )}
                {signatureMode === 'custom' && (
                  <>
                    <label htmlFor="email-signature-textarea" className="signature-textarea-label">
                      Signature text
                    </label>
                    <textarea
                      id="email-signature-textarea"
                      ref={signatureTextareaRef}
                      value={emailSignature}
                      onChange={(e) => {
                        setEmailSignature(e.target.value);
                        resizeSignatureTextarea();
                      }}
                      placeholder="Enter your email signature here..."
                      className="signature-textarea"
                    />
                  </>
                )}
                <div className="signature-actions">
                  <button
                    type="button"
                    className="btn btn--example"
                    onClick={handleUseProfileSignature}
                    title="Use your profile name and email"
                  >
                    <MdAutoAwesome size={16} />
                    Use My Details
                  </button>
                  <button
                    type="button"
                    className="btn btn--clear-signature"
                    onClick={handleClearSignature}
                    disabled={!emailSignature}
                    title="Clear signature"
                  >
                    <MdDelete size={16} />
                    Clear
                  </button>
                </div>
              </div>
            )
          )}

          {/* Save button - always visible */}
          <div className="signature-save-row">
            <button
              type="button"
              className={`btn btn--save-signature ${signatureSaved ? 'btn--save-signature--saved' : ''}`}
              onClick={handleSaveSignature}
              disabled={isSavingSignature}
              title="Save signature settings"
            >
              {isSavingSignature ? (
                <>Saving...</>
              ) : signatureSaved ? (
                <><MdCheck size={16} /> Saved</>
              ) : (
                <><MdSave size={16} /> Save Settings</>
              )}
            </button>
          </div>
        </div>

        <div className="settings-form">
          <h3>Profile</h3>
          <p className="settings-form__description">
            Display name and organization auto-save as you type.
            {autoSaveStatus === 'saving' && <span className="auto-save-status"> Saving...</span>}
            {autoSaveStatus === 'saved' && <span className="auto-save-status auto-save-status--saved"> <MdCheck size={14} /> Saved</span>}
          </p>
          <label>
            Display Name
            <input
              type="text"
              value={preferredDisplayName}
              onChange={handleDisplayNameChange}
              placeholder="Your name…"
            />
          </label>

          <label>
            Organization Name
            <input
              type="text"
              value={organizationName}
              onChange={handleOrgNameChange}
              placeholder="Your org…"
            />
          </label>

          <div className="profile-pic-row">
            <span className="profile-pic-label">Profile Picture</span>
            <div className="profile-pic-controls">
              {profilePic && (
                <>
                  <img
                    src={profilePic}
                    alt="Preview"
                    className={`profile-pic-preview profile-pic-preview--${profilePicShape}`}
                  />
                  <button
                    type="button"
                    className="btn btn--remove-pic"
                    onClick={handleRemovePic}
                  >
                    <MdDelete size={24}/>
                  </button>
                </>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
              />
            </div>
          </div>

          {showCropper && (
            <ProfilePictureCropper
              imageSrc={tempImageSrc}
              onCropComplete={handleCropComplete}
              onClose={() => {
                setShowCropper(false);
                setTempImageSrc('');
              }}
            />
          )}
        </div>

        {/* Sheet Access Section */}
        {/* A campaign's tracker is a Google Sheet shared with the sign-in
            address. When that address is not a Google account, the Sheet button
            lands on "You need access"; this is where the advisor lists the
            Google accounts they own. Saving is immediate — existing sheets are
            re-shared by the same call. */}
        {profileEmail && <SheetAccessAccounts signInEmail={profileEmail} />}

        {/* Blocked Contacts Section */}
        <div className="blocked-contacts-section">
          <h3>
            <MdBlock size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
            Blocked Contacts
          </h3>
          <p className="blocked-contacts-description">
            View and manage contacts who have opted out of receiving emails.
          </p>
          <button
            type="button"
            className="btn btn--blocked-contacts"
            onClick={() => setShowBlockedContacts(true)}
          >
            View Blocked Contacts
          </button>
        </div>
      </main>

      {/* Blocked Contacts Modal */}
      <BlockedContactsModal
        isOpen={showBlockedContacts}
        onClose={() => setShowBlockedContacts(false)}
      />
    </div>
  );
}
